import React, { FC, useEffect, useState } from 'react';
import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';
import * as TicketService from '@modules/ticket/service';
import { Diagnostic } from '@modules/diagnostics/model';
import { useAuthContext } from '@modules/auth/context';
import { renderOptional } from '@shared/utils/render';
import HeaderTitle from '@shared/components/header/HeaderTitle';
import { useTranslations } from '@shared/modules/translation/hooks';
import Seo from '@shared/components/seo/Seo';
import TicketSignIn from '@modules/ticket/components/sign-in/TicketSignIn';
import TicketDevice from '@modules/ticket/components/device/TicketDevice';
import TicketCreate from '@modules/ticket/components/create/TicketCreate';
import { pipe } from 'fp-ts/function';

interface TicketProps {
  diagnostic: Diagnostic;
  answerId: string;
}

const Ticket: FC<TicketProps> = ({ diagnostic, answerId }) => {
  const [serials, setSerials] = useState<Array<string>>([]);
  const [serialId, setSerialId] = useState<O.Option<string>>(O.none);

  const { profile } = useAuthContext();
  const translations = useTranslations();

  const isAuthenticated = O.isSome(profile);

  useEffect(() => {
    if (isAuthenticated) {
      pipe(
        TicketService.getSerials(null),
        TE.chainIOK(serials => () => setSerials(serials)),
      )();
    }
  }, [isAuthenticated]);

  return (
    <>
      <Seo title={translations.tickets.title} />

      <HeaderTitle title={translations.tickets.title} />

      {renderOptional(
        profile,
        profile =>
          renderOptional(
            serialId,
            serialId => (
              <TicketCreate
                serialId={serialId}
                answerId={answerId}
                diagnosticName={diagnostic.name}
                diagnosticNumber={diagnostic.number}
                email={profile.email}
                profile={profile}
              />
            ),
            () => <TicketDevice serials={serials} setSerialId={setSerialId} />,
          ),
        () => (
          <TicketSignIn referrer={`/diagnostics/${diagnostic.number}/failure/${answerId}`} />
        ),
      )}
    </>
  );
};

export default Ticket;
